import * as React from "react";
import { useState } from "react";
import styled from "@mui/material/styles/styled";
import { ReactComponent as ThanksIcon } from "../../assets/thanksEnterSymptomIcon.svg";
import { PatientColorMap } from "../../layout/patientColorMap";
import { PatientTextField } from "../uiControllers/patientTextField";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useWidgetSettings } from "../../widget/useWidgetSettings";
import { makeStyles, createStyles } from "@mui/styles";
import { Theme } from "@mui/material";
import {
  CHAT_ELEMENT_HEIGHT,
  CHAT_ELEMENT_SPACING,
} from "../consts/sizeConsts";
import Button from "@mui/material/Button";
import { useTranslate } from "../../hooks/useTranslate";
import Grow from "@mui/material/Grow";
import {
  marginRightToMarginLeftOnRtl,
  onRtl,
} from "fe-web-components/rtl/rtlStyles";
import { useCommonClinicAnalyticsParams } from "../../clinic/useCommonClinicAnalyticsParams";

//region [[ Styles ]]
const EnterUserSymptomView = styled("div")(({ theme }) => ({
  marginTop: "30px",
  marginBottom: CHAT_ELEMENT_SPACING + "px",
}));

const InputContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: "30px",
}));

const ThanksContainer = styled("div")(({ theme }) => ({
  backgroundColor: "#F7F7F9",
  borderRadius: "30px",
  display: "flex",
  padding: "15px",
  alignItems: "center",
}));

const ThanksLabel = styled("div")(({ theme }) => ({
  fontSize: "15px",
  fontWeight: 500,
}));

const ThanksIconStyled = styled(ThanksIcon)({
  ...marginRightToMarginLeftOnRtl(10),
  "& path": {
    fill: PatientColorMap.Primary,
  },
});

const EnterSymptomLabel = styled("div")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 500,
  marginBottom: "20px",
  color: PatientColorMap.Primary,
}));

const SendButton = styled(Button)(({ theme }) => ({
  "&.send-button": {
    border: "2px solid " + PatientColorMap.OrganMapBackground,
    color: PatientColorMap.Primary,
    textTransform: "capitalize",
    width: "90px",
    borderRadius: "67px",
    position: "absolute",
    right: "16px",
    left: "unset",
    ...onRtl({
      left: "10px",
      right: "unset",
    }),
    top: "14px",
    background: "transparent",
    padding: "8px 0",
    boxShadow: "none",
  },
}));

const EnterSymptomInput = styled(PatientTextField)({});

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    input: {
      textAlign: "left",
      backgroundColor: "white",
      fontSize: "16px",
      width: "100%",
      fontWeight: 500,
      height: CHAT_ELEMENT_HEIGHT + 15,
      paddingLeft: "15px",
      paddingTop: "15px",
      color: "black",
      paddingBottom: "15px",
      borderRadius: "30px",
    },
  }),
);

//endregion [[ Styles ]]

//region [[ Props ]]

export type EnterUserSymptomTextOverride = {
  /***
   * A title which will be displayed on top of the input filed, default is "Let us know what symptom you were looking for"
   */
  titleText?: string;
  /***
   * The placeholder text which will be presented on the input, default is "Enter your symptom"
   */
  placeholderText?: string;
  /***
   * A Thanks message that will be presented after users had successfully sent us their input
   */
  thankYouText?: string;
  /***
   * Used in the search component to be displayed under the results with the text "Can't find your symptom? Add X"
   */
  cantFindText?: string;
};

export interface EnterUserSymptomProps extends EnterUserSymptomTextOverride {
  onSymptomSent: (selection: string) => void;
  userSymptom?: string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const EnterUserSymptom = ({ ...props }: EnterUserSymptomProps) => {
  const [symptomSent, setSymptomSent] = useState<boolean>(false);
  const [userSymptom, setUserSymptom] = useState<string>(
    props.userSymptom || "",
  );
  const analyticsParams = useCommonClinicAnalyticsParams();

  const { translate } = useTranslate();

  const classes = useStyles();
  const { partnerId } = useWidgetSettings();

  const onEnterSymptom = (event) => {
    setUserSymptom(event.target.value);
  };

  const onSend = (event) => {
    evt({
      category: CATEGORY.PATIENT_CHAT,
      action: ACTION.PATIENT_CANT_FIND_SYMPTOM,
      label: userSymptom,
      partnerId,
      props: analyticsParams,
    });
    setSymptomSent(true);
    props?.onSymptomSent?.(userSymptom);
  };

  const { titleText, placeholderText, thankYouText } = props || {};

  return (
    <EnterUserSymptomView className={"enter-user-symptom-view"}>
      <EnterSymptomLabel className={"enter-symptom-label"}>
        {translate(titleText || "ENTER_SYMPTOM_DESCRIPTION")}
      </EnterSymptomLabel>
      <Grow timeout={100} in={!symptomSent} mountOnEnter unmountOnExit>
        <InputContainer>
          <EnterSymptomInput
            value={userSymptom || ""}
            InputProps={{
              classes: {
                root: classes.input,
              },
            }}
            placeholder={translate(placeholderText || "ENTER_YOUR_SYMPTOM")}
            onBlur={onEnterSymptom}
            onChange={onEnterSymptom}
            variant="outlined"
            style={{ width: "100%" }}
            id="outlined-basic"
            autoFocus
          />
          <SendButton className={"send-button"} onClick={onSend}>
            {translate("SEND")}
          </SendButton>
        </InputContainer>
      </Grow>
      <Grow timeout={500} in={symptomSent} mountOnEnter unmountOnExit>
        <ThanksContainer>
          <ThanksIconStyled></ThanksIconStyled>
          <ThanksLabel>
            {translate(thankYouText || "THANKS_ENTER_SYMPTOM")}
          </ThanksLabel>
        </ThanksContainer>
      </Grow>
    </EnterUserSymptomView>
  );
};
